import { type DrawConfig, WEEKDAYS, type BettingConfig, type CloseTime, type GameConfig, type PlannedDraw, type ProductConfig } from '@lp/contracts';
import { num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge } from './ui';

const DAY_LABEL = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function scheduleText(g: GameConfig, t: (s: string) => string): string {
  if (g.schedule.kind === 'monthly') return `${t('Dates of each month')}: ${g.schedule.dates.join(', ')}`;
  if (g.schedule.days.length === 7) return t('Every day');
  return g.schedule.days.map((d) => t(DAY_LABEL[d]!)).join(', ');
}

function CloseCell({ at }: { at: CloseTime }) {
  const t = useT();
  if (typeof at === 'string') return <span className="font-mono">{at}</span>;
  const same = WEEKDAYS.every((d) => at[d] === at.mon);
  if (same) return <span className="font-mono">{at.mon}</span>;
  // Monday first, as the owner reads a week
  const order = [...WEEKDAYS.slice(1), WEEKDAYS[0]];
  return (
    <span className="inline-grid grid-cols-7 gap-x-2 text-[11px]">
      {order.map((d) => (
        <span key={d} className="text-center text-ink-500">
          {t(DAY_LABEL[WEEKDAYS.indexOf(d)]!)}
        </span>
      ))}
      {order.map((d) => (
        <span key={d} className="text-center font-mono text-ink-800">
          {at[d]}
        </span>
      ))}
    </span>
  );
}

/** a game's betting rules in one block: what a winning number pays, what a post costs, what cannot be bet, the amounts */
export function BettingSummary({ b }: { b: BettingConfig }) {
  const t = useT();
  const costs = Object.entries(b.multipliers).map(([p, m]) => `${p} ${[m['2'] ? `2D ×${m['2']}` : '', m['3'] ? `3D ×${m['3']}` : ''].filter(Boolean).join(' ')}`);
  const special = Object.entries(b.payoutByPost).map(([p, m]) => `${p} ${[m['2'] ? `2D ×${m['2']}` : '', m['3'] ? `3D ×${m['3']}` : ''].filter(Boolean).join(' ')}`);
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-ink-100 bg-ink-50/50 px-3 py-2 text-xs" data-testid="betting-summary">
      <span>
        <span className="text-ink-500">{t('Pays')}:</span> 2D ×{b.payout['2']} · 3D ×{b.payout['3']}
        {special.length > 0 && <span className="text-ink-500"> ({special.join(', ')})</span>}
      </span>
      <span>
        <span className="text-ink-500">{t('Costs more')}:</span> <span className="font-mono">{costs.length ? costs.join(' · ') : '—'}</span>
      </span>
      {b.refused.length > 0 && (
        <span>
          <span className="text-ink-500">{t('Not sold')}:</span> <span className="font-mono">{b.refused.map((r) => `${r.post} ${r.digits}D`).join(' · ')}</span>
        </span>
      )}
      <span>
        <span className="text-ink-500">{t('Amount per number')}:</span> {num(b.minAmount)} – {num(b.maxAmountPerNumber)} ៛, {t('step')} {num(b.amountStep)}
      </span>
    </div>
  );
}

/** what a draw's result follows when it is not its game's pattern: its own pattern, typed by hand, or another draw's result */
export function DrawResultBadge({ d }: { d: DrawConfig }) {
  const t = useT();
  if (d.resultAs)
    return (
      <span className="mt-0.5 block font-sans text-[10px] font-normal text-brand-700" data-testid={`result-as-${d.time}`}>
        = {d.resultAs.product} {d.resultAs.game} {d.resultAs.time}
      </span>
    );
  if (d.rule === undefined) return null;
  return <span className="mt-0.5 block font-sans text-[10px] font-normal text-emerald-700">{d.rule ? `${t('Pattern')}: ${d.rule}` : t('Every post typed by hand')}</span>;
}

/** what is not confirmed yet — the owner gives the source; nothing is guessed */
export function ConfigGaps({ config }: { config: ProductConfig }) {
  const t = useT();
  if (!config.gaps.length) return null;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs" data-testid="config-gaps">
      <div className="mb-1 font-semibold text-amber-800">{t('Configuration gaps — waiting for their source')}</div>
      <ul className="space-y-1">
        {config.gaps.map((g, i) => (
          <li key={i}>
            <span className="font-mono font-medium">
              {g.game}
              {g.time ? ` ${g.time}` : ''}
            </span>{' '}
            {g.what}
            {g.source && (
              <span className="text-ink-500">
                {' '}
                ({t('source')}: {g.source})
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** a product version, read-only: its games, draws, posts and close times — defaults marked "to confirm" */
export function ProductConfigView({ config }: { config: ProductConfig }) {
  const t = useT();
  const allPosts = config.posts.map((p) => p.code);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <span>
          <span className="text-ink-500">{t('Time zone')}:</span> {config.timezone}
        </span>
        <span>
          <span className="text-ink-500">{t('Posts')}:</span> <span className="font-mono">{allPosts.join(' ')}</span>
        </span>
        <span>
          <span className="text-ink-500">{t('Digits')}:</span> {config.digits.map((d) => `${d}D`).join(' · ')}
        </span>
      </div>
      <ConfigGaps config={config} />
      {config.games.map((g) => (
        <section key={g.code} className="rounded-md border border-ink-200" data-testid={`game-${g.code}`}>
          <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-ink-100 bg-ink-50 px-3 py-2 text-sm">
            <span className="font-semibold text-ink-900">{g.nameEn}</span>
            <span className="text-ink-500">{g.nameKm}</span>
            <span className="font-mono text-xs text-ink-500">{g.code}</span>
            <span className="text-xs text-ink-600">{scheduleText(g, t)}</span>
            {g.rule ? <Badge tone="green">{t('Pattern')}: {g.rule}</Badge> : <Badge>{t('Every post typed by hand')}</Badge>}
          </header>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-ink-100 text-left text-ink-500">
                <th className="w-16 px-3 py-1.5">{t('Draw')}</th>
                <th className="px-3 py-1.5">{t('Posts')}</th>
                <th className="px-3 py-1.5">{t('Betting closes')}</th>
              </tr>
            </thead>
            <tbody>
              {g.draws.map((d) => {
                const offered = d.posts ?? allPosts;
                const listed = new Set(d.closes.flatMap((c) => c.posts));
                const rest = offered.filter((p) => !listed.has(p));
                return (
                  <tr key={d.time} className="border-b border-ink-100 align-top last:border-0">
                    <td className="px-3 py-1.5 font-mono font-medium">
                      {d.time}
                      <DrawResultBadge d={d} />
                    </td>
                    <td className="px-3 py-1.5 font-mono">{offered.join(' ')}</td>
                    <td className="space-y-1 px-3 py-1.5">
                      {d.closes.map((c, i) => (
                        <div key={i} className="flex flex-wrap items-baseline gap-2">
                          <span className="min-w-16 font-mono text-ink-600">{c.posts.join(' ')}</span>
                          <CloseCell at={c.at} />
                        </div>
                      ))}
                      {rest.length > 0 && (
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="min-w-16 font-mono text-ink-600">{rest.join(' ')}</span>
                          <span className="text-amber-700">
                            {g.defaultCloseMinutes} {t('min before the draw')} · {t('default — to confirm')}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <BettingSummary b={g.betting} />
        </section>
      ))}
    </div>
  );
}

/** planned draws grouped by day (Cambodia time), with the product's own time when its zone differs */
export function PlannedDraws({ draws, timezone }: { draws: PlannedDraw[]; timezone: string }) {
  const t = useT();
  const days = [...new Set(draws.map((d) => d.datePP))];
  if (!draws.length) return <p className="text-sm text-ink-600">{t('No draw in these days.')}</p>;
  return (
    <div className="space-y-3">
      {days.map((day) => (
        <div key={day}>
          <div className="mb-1 text-xs font-semibold text-ink-700">{day}</div>
          <div className="flex flex-wrap gap-1.5">
            {draws
              .filter((d) => d.datePP === day)
              .map((d) => {
                const closes = d.posts.map((p) => p.closePP.slice(11)).sort();
                const anyDefault = d.posts.some((p) => p.defaultClose);
                return (
                  <span key={`${d.gameCode}-${d.at}`} className="rounded-md border border-ink-200 bg-white px-2 py-1 text-xs" title={d.posts.map((p) => `${p.code} ${p.closePP.slice(11)}${p.defaultClose ? ' *' : ''}`).join('\n')}>
                    <span className="font-mono font-semibold">{d.timePP}</span> <span className="font-mono text-ink-500">{d.gameCode}</span>
                    {timezone !== 'Asia/Phnom_Penh' && <span className="ml-1 text-ink-500">({d.time} {t('local')})</span>}
                    <span className="ml-1 text-ink-500">
                      {t('closes')} {closes[0] === closes[closes.length - 1] ? closes[0] : `${closes[0]}–${closes[closes.length - 1]}`}
                      {anyDefault && <span className="text-amber-700"> *</span>}
                    </span>
                    {d.exception && (
                      <span className="ml-1">
                        <Badge tone="blue">{t(d.exception === 'moved' ? 'Moved' : 'Extra')}</Badge>
                      </span>
                    )}
                  </span>
                );
              })}
          </div>
        </div>
      ))}
      <p className="text-xs text-ink-500">
        {t('Times are Cambodia time.')} <span className="text-amber-700">*</span> {t('default close — to confirm')}
      </p>
    </div>
  );
}
